import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiDownload,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiActivity,
  FiSearch,
  FiMapPin,
  FiRefreshCw,
  FiLogIn,
  FiLogOut,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/AttendanceDashboard.css';

const TIME_CARDS_QUERY = `
  query AttendanceTimeCards($date: String) {
    findTimeCards(date: $date) {
      id
      userId
      date
      timeIn
      timeOut
      duration
      leaveType
      notes
      approvalStatus
      latitude
      longitude
      user {
        id
        firstName
        lastName
        employeeCode
        roles
        jobTitle
        typeOfWork
        email
      }
    }
  }
`;

const USERS_QUERY = `
  query AttendanceUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        roles
        jobTitle
        typeOfWork
        employmentType
        employeeCode
        isActive
        contact
        address
        branchId
      }
      totalPages
    }
  }
`;

const LEAVE_QUERY = `
  query AttendanceLeaveRequests {
    leaveRequests {
      id
      name
      employeeId
      leaveType
      status
      startDate
      endDate
    }
  }
`;

const PROJECTS_QUERY = `
  query AttendanceProjects($limit: Int) {
    findAllProject(limit: $limit) {
      id
      name
      startDate
      deadLine
      status
      booking { fullName address }
      manager { id firstName lastName }
    }
  }
`;

const ROSTER_QUERY = `
  query AttendanceRosterJobs {
    getAdminRosterTasks {
      id
      date
      status
      user { id }
      assignees { id }
      project {
        id
        name
        startDate
        booking { fullName address }
      }
    }
  }
`;

const ROLE_TABS = [
  'All',
  'Manager',
  'Supervisor',
  'Cleaner',
  'Employee',
  'Contractor',
];

const AVATARS = ['#60a5fa', '#34d399', '#a78bfa', '#f472b6', '#fb923c', '#38bdf8'];

function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}

function avatarColor(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % AVATARS.length;
  return AVATARS[h];
}

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDayLabel(iso) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function cardDateISO(raw) {
  if (!raw) return null;
  const s = String(raw);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

function formatClock(value) {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${String(match[1]).padStart(2, '0')}:${match[2]}`;
}

function isOpenClock(card) {
  if (!card?.timeIn) return false;
  const notes = String(card.notes || '');
  if (notes.includes('[CLOCK_OPEN]')) return true;
  try {
    const parsed = JSON.parse(notes);
    const type = String(parsed?.type || '').toLowerCase();
    if (type.includes('open')) return true;
    if (type.includes('report')) return false;
  } catch {
    /* ignore */
  }
  const inn = formatClock(card.timeIn);
  const out = formatClock(card.timeOut);
  return Boolean(inn && out && inn === out);
}

function parseNotes(notes) {
  const raw = String(notes || '');
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        projectId: parsed.projectId || '',
        projectName: parsed.projectName || parsed.projectCode || '',
        location: parsed.location || parsed.address || '',
      };
    }
  } catch {
    /* fall through */
  }
  const projectMatch = raw.match(/\[PROJECT:([^\]]+)\]/i);
  const nameMatch = raw.match(/\[PROJECT_NAME:([^\]]+)\]/i);
  const locMatch = raw.match(/\[LOCATION:([^\]]+)\]/i);
  return {
    projectId: projectMatch?.[1]?.trim() || '',
    projectName: nameMatch?.[1]?.trim() || '',
    location: locMatch?.[1]?.trim() || '',
  };
}

function durationLabel(card, open) {
  if (!card) return '—';
  if (open) {
    const inn = formatClock(card.timeIn);
    if (!inn) return 'In progress';
    const [h, m] = inn.split(':').map(Number);
    const start = new Date();
    start.setHours(h, m, 0, 0);
    const mins = Math.max(0, Math.round((Date.now() - start.getTime()) / 60000));
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${hh}h ${String(mm).padStart(2, '0')}m (live)`;
  }
  if (card.duration) return card.duration;
  const inn = formatClock(card.timeIn);
  const out = formatClock(card.timeOut);
  if (!inn || !out || inn === out) return '—';
  const [ih, im] = inn.split(':').map(Number);
  const [oh, om] = out.split(':').map(Number);
  let mins = oh * 60 + om - (ih * 60 + im);
  if (mins < 0) mins += 24 * 60;
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  return `${hh}h ${String(mm).padStart(2, '0')}m`;
}

function workforceRole(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : user?.roles ? [user.roles] : [];
  const hay = `${roles.join(' ')} ${user?.jobTitle || ''} ${user?.typeOfWork || ''} ${user?.employmentType || ''}`.toLowerCase();
  if (/contractor/.test(hay)) return 'Contractor';
  if (/supervisor/.test(hay)) return 'Supervisor';
  if (/manager|operations/.test(hay)) return 'Manager';
  if (/cleaner|field/.test(hay)) return 'Cleaner';
  if (/admin|software/.test(hay)) return 'Employee';
  return 'Employee';
}

function isOnLeaveForDate(leave, isoDate) {
  if (String(leave.status || '').toLowerCase() !== 'approved') return false;
  const start = leave.startDate ? new Date(leave.startDate) : null;
  const end = leave.endDate ? new Date(leave.endDate) : start;
  if (!start || Number.isNaN(start.getTime())) return false;
  const day = new Date(`${isoDate}T12:00:00`);
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end && !Number.isNaN(end.getTime()) ? end : start);
  e.setHours(23, 59, 59, 999);
  return day >= s && day <= e;
}

function deriveStatus(card, onLeave) {
  if (onLeave) return 'on_leave';
  if (!card) return 'not_clocked_in';
  const open = isOpenClock(card);
  const late = String(card.leaveType || '')
    .toLowerCase()
    .includes('late');
  if (late && open) return 'late';
  if (open) return 'clocked_in';
  if (card.timeIn) return 'clocked_out';
  return 'not_clocked_in';
}

const STATUS_LABEL = {
  clocked_in: 'Clocked In',
  clocked_out: 'Clocked Out',
  late: 'Late',
  on_leave: 'On Leave',
  not_clocked_in: 'Not clocked in',
  absent: 'Absent',
};

async function fetchAllUsers() {
  const all = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages && page <= 20) {
    const data = await graphqlRequest(USERS_QUERY, { page, limit: 100 });
    const result = data?.getAllUser;
    totalPages = Math.max(1, Number(result?.totalPages) || 1);
    all.push(...(result?.data || []));
    page += 1;
  }
  return all;
}

function buildJobByUser(projects, rosterTasks, isoDate) {
  const map = new Map();
  (projects || []).forEach((p) => {
    if (!statusOk(p.status)) return;
    const start = cardDateISO(p.startDate);
    const end = cardDateISO(p.deadLine) || start;
    const inRange =
      start &&
      ((!end && start === isoDate) ||
        (end && isoDate >= start && isoDate <= end) ||
        start === isoDate);
    if (!inRange || !p.manager?.id) return;
    map.set(p.manager.id, {
      job: p.name,
      site: p.booking?.fullName || p.name || '',
      address: p.booking?.address || '',
    });
  });
  (rosterTasks || []).forEach((t) => {
    const uids = [
      ...new Set(
        [...(t.assignees || []), t.user]
          .map((u) => u?.id)
          .filter(Boolean),
      ),
    ];
    if (!uids.length) return;
    const td =
      cardDateISO(t.date) || cardDateISO(t.project?.startDate);
    if (td && td !== isoDate) return;
    uids.forEach((uid) => {
      if (map.has(uid)) return;
      map.set(uid, {
        job: t.project?.name || 'Assigned task',
        site: t.project?.booking?.fullName || '',
        address: t.project?.booking?.address || '',
      });
    });
  });
  return map;
}

function statusOk(status) {
  const s = String(status || '').toLowerCase();
  return !['cancel', 'cancelled', 'rejected'].includes(s);
}

export default function AttendanceDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [live, setLive] = useState(true);
  const [date, setDate] = useState(() => todayISO());

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const [users, tcData, leaveData, projectsData, rosterData] =
          await Promise.all([
          fetchAllUsers().catch((err) => {
            toast.error(err?.message || 'Failed to load employees');
            return [];
          }),
          graphqlRequest(TIME_CARDS_QUERY, { date }).catch(async (err) => {
            if (String(err?.message || '').toLowerCase().includes('latitude')) {
              const fallback = `
                query AttendanceTimeCardsNoGps($date: String) {
                  findTimeCards(date: $date) {
                    id userId date timeIn timeOut duration leaveType notes approvalStatus
                    user { id firstName lastName employeeCode roles jobTitle typeOfWork email }
                  }
                }
              `;
              return graphqlRequest(fallback, { date });
            }
            return { findTimeCards: [] };
          }),
          graphqlRequest(LEAVE_QUERY).catch(() => ({ leaveRequests: [] })),
          graphqlRequest(PROJECTS_QUERY, { limit: 300 }).catch(() => ({
            findAllProject: [],
          })),
          graphqlRequest(ROSTER_QUERY).catch(() => ({
            getAdminRosterTasks: [],
          })),
        ]);

        const cards = (Array.isArray(tcData?.findTimeCards)
          ? tcData.findTimeCards
          : []
        ).filter((c) => {
          const day = cardDateISO(c.date);
          return !day || day === date;
        });

        const cardByUser = new Map();
        cards.forEach((c) => {
          if (!c.userId) return;
          const prev = cardByUser.get(c.userId);
          if (!prev || isOpenClock(c)) cardByUser.set(c.userId, c);
        });

        const leaves = (leaveData?.leaveRequests || []).filter((l) =>
          isOnLeaveForDate(l, date),
        );
        const leaveKeys = new Set(
          leaves.flatMap((l) => [
            String(l.employeeId || '').toLowerCase(),
            String(l.name || '').toLowerCase(),
          ]),
        );

        const jobByUser = buildJobByUser(
          projectsData?.findAllProject || [],
          rosterData?.getAdminRosterTasks || [],
          date,
        );

        const seen = new Set();
        const mapped = (users || [])
          .filter((u) => u?.isActive !== false)
          .map((u) => {
            seen.add(u.id);
            const name =
              `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
              u.email ||
              'Unknown';
            const role = workforceRole(u);
            const card = cardByUser.get(u.id);
            const onLeave =
              leaveKeys.has(String(u.id).toLowerCase()) ||
              leaveKeys.has(name.toLowerCase()) ||
              leaveKeys.has(String(u.employeeCode || '').toLowerCase());
            const open = isOpenClock(card);
            const status = deriveStatus(card, onLeave);
            const notes = parseNotes(card?.notes);
            const jobInfo = jobByUser.get(u.id);
            const hasGps =
              card?.latitude != null &&
              card?.longitude != null &&
              Number.isFinite(Number(card.latitude));

            return {
              id: u.id,
              name,
              email: u.email || '—',
              code: u.employeeCode || '—',
              role,
              job:
                notes.projectName ||
                jobInfo?.job ||
                (onLeave
                  ? leaves.find(
                      (l) =>
                        String(l.employeeId).toLowerCase() ===
                          String(u.id).toLowerCase() ||
                        String(l.name).toLowerCase() === name.toLowerCase(),
                    )?.leaveType || 'Leave'
                  : '—'),
              site:
                notes.location ||
                jobInfo?.site ||
                jobInfo?.address ||
                u.address ||
                '—',
              clockIn: formatClock(card?.timeIn) || '—',
              clockOut: open
                ? '—'
                : formatClock(card?.timeOut) &&
                    formatClock(card?.timeOut) !== formatClock(card?.timeIn)
                  ? formatClock(card?.timeOut)
                  : '—',
              duration: durationLabel(card, open),
              gps: hasGps ? 'Verified' : card?.timeIn ? 'No pin' : '—',
              gpsCoords: hasGps
                ? `${Number(card.latitude).toFixed(5)}, ${Number(
                    card.longitude,
                  ).toFixed(5)}`
                : '',
              approval: card?.approvalStatus || '—',
              status,
              open,
              avatar: avatarColor(name),
              hasCard: Boolean(card),
            };
          });

        // Time cards for people not in user list (edge cases)
        cards.forEach((card) => {
          if (seen.has(card.userId)) return;
          const name =
            `${card.user?.firstName || ''} ${card.user?.lastName || ''}`.trim() ||
            'Unknown';
          const role = workforceRole(card.user || {});
          const open = isOpenClock(card);
          const notes = parseNotes(card.notes);
          const hasGps =
            card.latitude != null &&
            card.longitude != null &&
            Number.isFinite(Number(card.latitude));
          mapped.push({
            id: card.userId || card.id,
            name,
            email: card.user?.email || '—',
            code: card.user?.employeeCode || '—',
            role,
            job: notes.projectName || '—',
            site: notes.location || '—',
            clockIn: formatClock(card.timeIn) || '—',
            clockOut: open
              ? '—'
              : formatClock(card.timeOut) &&
                  formatClock(card.timeOut) !== formatClock(card.timeIn)
                ? formatClock(card.timeOut)
                : '—',
            duration: durationLabel(card, open),
            gps: hasGps ? 'Verified' : '—',
            gpsCoords: hasGps
              ? `${Number(card.latitude).toFixed(5)}, ${Number(
                  card.longitude,
                ).toFixed(5)}`
              : '',
            approval: card.approvalStatus || '—',
            status: deriveStatus(card, false),
            open,
            avatar: avatarColor(name),
            hasCard: true,
          });
        });

        // Leave-only people missing from users
        leaves.forEach((leave) => {
          const already = mapped.some(
            (r) =>
              r.name.toLowerCase() === String(leave.name || '').toLowerCase() ||
              r.id === leave.employeeId,
          );
          if (already) return;
          const name = leave.name || 'Employee';
          mapped.push({
            id: `leave-${leave.id}`,
            name,
            email: '—',
            code: leave.employeeId || '—',
            role: 'Employee',
            job: leave.leaveType || 'Leave',
            site: '—',
            clockIn: '—',
            clockOut: '—',
            duration: '—',
            gps: '—',
            gpsCoords: '',
            approval: '—',
            status: 'on_leave',
            open: false,
            avatar: avatarColor(name),
            hasCard: false,
          });
        });

        mapped.sort((a, b) => {
          const rank = {
            clocked_in: 0,
            late: 1,
            clocked_out: 2,
            on_leave: 3,
            not_clocked_in: 4,
          };
          const d = (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
          if (d !== 0) return d;
          return a.name.localeCompare(b.name);
        });

        setRows(mapped);
      } catch (err) {
        console.error(err);
        toast.error(err?.message || 'Failed to load attendance.');
        setRows([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [date],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => void load(true), 60000);
    return () => clearInterval(id);
  }, [live, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter !== 'All' && r.role !== roleFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.role.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.job.toLowerCase().includes(q) ||
        r.site.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q)
      );
    });
  }, [rows, search, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const base = rows;
    return [
      {
        id: 'in',
        label: 'Clocked In',
        value: base.filter((r) => r.status === 'clocked_in').length,
        icon: FiLogIn,
        tone: 'green',
      },
      {
        id: 'out',
        label: 'Clocked Out',
        value: base.filter((r) => r.status === 'clocked_out').length,
        icon: FiLogOut,
        tone: 'blue',
      },
      {
        id: 'late',
        label: 'Late',
        value: base.filter((r) => r.status === 'late').length,
        icon: FiClock,
        tone: 'orange',
      },
      {
        id: 'absent',
        label: 'Not clocked in',
        value: base.filter((r) => r.status === 'not_clocked_in').length,
        icon: FiXCircle,
        tone: 'red',
      },
      {
        id: 'leave',
        label: 'On Leave',
        value: base.filter((r) => r.status === 'on_leave').length,
        icon: FiActivity,
        tone: 'purple',
      },
      {
        id: 'team',
        label: 'Workforce',
        value: base.length,
        icon: FiCheckCircle,
        tone: 'slate',
      },
    ];
  }, [rows]);

  const roleCounts = useMemo(() => {
    const counts = { All: rows.length };
    ROLE_TABS.slice(1).forEach((r) => {
      counts[r] = rows.filter((x) => x.role === r).length;
    });
    return counts;
  }, [rows]);

  const exportCsv = useCallback(() => {
    const cols = [
      'Name',
      'Role',
      'Code',
      'Email',
      'Job / Project',
      'Site',
      'Clock In',
      'Clock Out',
      'Duration',
      'GPS',
      'GPS Coords',
      'Approval',
      'Status',
      'Date',
    ];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.join(',')];
    filtered.forEach((r) => {
      lines.push(
        [
          r.name,
          r.role,
          r.code,
          r.email,
          r.job,
          r.site,
          r.clockIn,
          r.clockOut,
          r.duration,
          r.gps,
          r.gpsCoords,
          r.approval,
          STATUS_LABEL[r.status] || r.status,
          date,
        ]
          .map(escape)
          .join(','),
      );
    });
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered, date]);

  return (
    <div className="at-page">
      <header className="at-header">
        <div>
          <h1>Attendance</h1>
          <p>
            {live ? 'Live' : 'Paused'} · {formatDayLabel(date)} · Managers,
            supervisors, cleaners, employees &amp; contractors
          </p>
        </div>
        <div className="at-header-actions">
          <label className="at-date">
            <span>Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayISO())}
            />
          </label>
          <button
            type="button"
            className={`at-live${live ? ' is-on' : ''}`}
            onClick={() => setLive((v) => !v)}
          >
            <span className="at-dot" />
            Live
          </button>
          <button
            type="button"
            className="at-filter"
            onClick={() => void load(true)}
            disabled={loading || refreshing}
          >
            <FiRefreshCw className={refreshing ? 'is-spin' : ''} />
            Refresh
          </button>
          <button type="button" className="at-export" onClick={exportCsv}>
            <FiDownload />
            Export
          </button>
        </div>
      </header>

      <section className="at-stats at-stats--6">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.id} className="at-stat">
              <div className={`at-stat-icon at-stat-icon--${s.tone}`}>
                <Icon />
              </div>
              <div>
                <div className="at-stat-value">{s.value}</div>
                <div className="at-stat-label">{s.label}</div>
              </div>
            </article>
          );
        })}
      </section>

      <div className="at-role-row">
        {ROLE_TABS.map((r) => (
          <button
            key={r}
            type="button"
            className={`at-role-chip ${roleFilter === r ? 'is-active' : ''}`}
            onClick={() => setRoleFilter(r)}
          >
            {r}
            <span>{roleCounts[r] ?? 0}</span>
          </button>
        ))}
      </div>

      <section className="at-panel">
        <div className="at-panel-top">
          <h2>Attendance log</h2>
          <div className="at-tools">
            <label className="at-search">
              <FiSearch />
              <input
                type="search"
                placeholder="Search name, role, job, code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <select
              className="at-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="clocked_in">Clocked In</option>
              <option value="clocked_out">Clocked Out</option>
              <option value="late">Late</option>
              <option value="not_clocked_in">Not clocked in</option>
              <option value="on_leave">On Leave</option>
            </select>
          </div>
        </div>

        <div className="at-table-wrap">
          <table className="at-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Job / Site</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Duration</th>
                <th>GPS</th>
                <th>Approval</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="at-empty">
                    Loading attendance for everyone…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="at-empty">
                    No people match this filter for {formatDayLabel(date)}.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="at-employee">
                        <span
                          className="at-avatar"
                          style={{ background: row.avatar }}
                        >
                          {initials(row.name)}
                        </span>
                        <div>
                          <div className="at-name">{row.name}</div>
                          <div className="at-loc">
                            {row.code !== '—' ? row.code : row.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`at-role at-role--${row.role.toLowerCase()}`}>
                        {row.role}
                      </span>
                    </td>
                    <td>
                      <div className="at-job">{row.job}</div>
                      <div className="at-loc">{row.site}</div>
                    </td>
                    <td className="at-time">{row.clockIn}</td>
                    <td className="at-time">{row.clockOut}</td>
                    <td>{row.duration}</td>
                    <td>
                      {row.gps === 'Verified' ? (
                        <span className="at-gps" title={row.gpsCoords}>
                          <FiMapPin /> Verified
                        </span>
                      ) : (
                        row.gps
                      )}
                    </td>
                    <td>
                      <span className="at-approval">{row.approval}</span>
                    </td>
                    <td>
                      <span className={`at-badge at-badge--${row.status}`}>
                        {STATUS_LABEL[row.status] || row.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
